import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  TJobResourceLinkItem,
  TJobResourceLinksProps,
} from '@/console/features/jobs/components/job-resource-links/job-resource-links.types';
import { CustomValue } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.styled';
import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { Tag } from '@/shared/components/ui/tag/tag';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { getProtectedResourceLabel } from '@/shared/utils/protected-resource-label';

const NAME_MAX_LENGTH = 32;

// Renders the resources a job links to: the first inline, the rest behind a
// "+n" tag so a job touching many devices stays one row high.
export const JobResourceLinks: React.FC<TJobResourceLinksProps> = ({
  ids,
  namesById,
  isPending,
  isProtected,
  resourceType,
  deletedLabel,
  icon: Icon,
  getPath,
  truncate,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const resolveItem = (id: string): TJobResourceLinkItem => {
    const name = namesById[id];

    if (name) {
      return { id, name, isResolved: true, isProtected: false };
    }

    return {
      id,
      name: isProtected
        ? getProtectedResourceLabel(t, resourceType)
        : deletedLabel,
      isResolved: false,
      isProtected,
    };
  };

  const renderItem = (item: TJobResourceLinkItem, truncateName: boolean) => {
    const navigateToItem = () => {
      if (!item.isResolved) {
        return;
      }
      navigate(getPath(item.id));
    };

    const renderName = () => {
      if (truncateName) {
        return (
          <TruncatedText
            variant="p7"
            maxLength={NAME_MAX_LENGTH}
            text={item.name}
          />
        );
      }
      return <Typography variant="p7">{item.name}</Typography>;
    };

    return (
      <CustomValue onClick={navigateToItem} $clickable={item.isResolved}>
        <Icon size="1rem" variant={item.isProtected ? 'light' : 'primary'} />
        {renderName()}
      </CustomValue>
    );
  };

  if (!ids?.length) {
    return null;
  }

  if (isPending) {
    return <Skeleton />;
  }

  const [firstItem, ...remainingItems] = ids.map(resolveItem);

  // Names are not truncated here: the tooltip is the place to read them in full.
  const renderRemainingItems = () => (
    <Stack spacing="0.25rem" py="0.25rem">
      {remainingItems.map((item) => (
        <React.Fragment key={item.id}>{renderItem(item, false)}</React.Fragment>
      ))}
    </Stack>
  );

  return (
    <Stack direction="row" alignItems="center" gap="0.375rem">
      {renderItem(firstItem, Boolean(truncate))}
      {remainingItems.length > 0 && (
        <Tooltip title={renderRemainingItems()} wrapper="default">
          <Tag label={`+${remainingItems.length}`} />
        </Tooltip>
      )}
    </Stack>
  );
};
